import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  TextField,
  Paper,
  useMediaQuery,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AIChatbotApiService from '../services/AIChatbotApiService';
import { useLocation, useNavigate } from 'react-router-dom';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import DebugService from "../services/DebugService";
import { STORAGE_KEYS, getScopedItem, setScopedItem, generateAiChatBotMsgId, AI_CHATBOT_DISABLED_ROUTES } from "./common/Helper";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TopQuestionsLoader from '../components/common/contentloader/TopQuestionsLoader';  // ← adjust path

/**
 * @description Format AI response into a readable multiline string
 * Flattens nested objects and arrays into key-value text lines
 * for chat display
 * @author Ashish Sharma
 */
const extractFiveObjectArray = (input) => {
  let result = null
  const isFiveObjectArray = (val) =>
    Array.isArray(val) &&
    val.length === 5 &&
    val.every(item => item && typeof item === 'object' && !Array.isArray(item))
  const traverse = (value) => {
    if (!value || result) return;
    if (isFiveObjectArray(value)) {
      result = value;
      return;
    }
    if (Array.isArray(value)) value.forEach(traverse);
    else if (typeof value === 'object') Object.values(value).forEach(traverse);
  }
  traverse(input);
  return result ?? [];
}

const formatAIResponse = (response) => {
  // Case 1: Simple markdown/string response (most common case)
  if (response?.message) {
    return response.message;
  }

  // Case 2: Top 5 questions suggestion (initial load or special prompt)
  if (response && typeof response === 'object') {
    const fiveItems = extractFiveObjectArray(response);

    if (fiveItems.length === 5) {

      const questions = fiveItems
        .map(item => {
          return item?.question;
        })
        .filter(q => q.length > 0); 

      if (questions.length > 0) {
        return (
          "**Suggested questions:**\n\n" +
          questions
            .map((q, i) => `${i + 1}. ${q}`)
            .join('\n')
        );
      }
    }
  }
  return 'Something went wrong. Please try again.';
};

const isTopLevelEmpty = (obj) =>
  !obj ||
  Object.values(obj).every(
    (v) =>
      (Array.isArray(v) && v.length === 0) ||
      (v && typeof v === 'object' && Object.keys(v).length === 0) ||
      v == null
);

const AIChatbot = ({ open, onClose, autoSendPrompt, autoSendJsonData, modalMode }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();
  const location = useLocation();

  const messagesEndRef = useRef(null);
  const autoSendFiredRef = useRef(false);

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [providerKeys, setProviderKeys] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  const [currentPath, setCurrentPath] = useState(null);  
  const hasProviders = models.length > 0;
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  /**
  * @description Fetch AI providers and their access keys
  * Builds provider-accessKey mapping and provider dropdown options
  * @author Ashish Sharma
  */
  const fetchProviders = async () => {
    try {
      const keys = await AIChatbotApiService.getAiChatbotAccessKeys();
      const providerMap = {};
      const providers = [];
      keys.forEach(item => {
        providerMap[item.provider] = item.accessKey;
        if (!providers.some(p => p.value === item.provider)) {
          providers.push({ value: item.provider, label: item.provider });
        }
      });

      setProviderKeys(providerMap);
      setModels(providers);

      if (providers.length > 0) {
        const preferred = providers.find(p => p.value === 'OPENROUTER') ?? providers[0];
        setSelectedModel(preferred.value);
      }
    } catch (err) {
      DebugService.error('Failed to load AI providers', err);
    }
  }

  /**
  * @description Fetch AI-suggested questions when loaded initially
  * @author Ashish Sharma
  */
  const fetchTopQuestions = async (currentCache = {}) => {
    try {
      const response = await AIChatbotApiService.askAiChatbot({
        accessKey: providerKeys[selectedModel],
        provider: selectedModel,
        question: `Can you suggest the top 5 questions based on the data I have provided?
          Return the response strictly in the following JSON format:
          {
            "top_questions": [
              {
                "question": "?",
                "answer": ""
              }
            ]
          }`,
        resType: 'json',
        jsonData: currentCache,
        sourceFrom: currentPath,
      });
      const items = extractFiveObjectArray(response)
      const formatted = items.map((item, i) => ({
        id: `ai-top-${i}`,
        text: item?.question ?? '',
        answer: item?.answer ?? '',
        sender: 'ai',
        isSuggestion: true,
        clicked: false,
      })).filter(item => item.text.length > 0);
      setMessages(formatted);
    } catch (err) {
      DebugService.error('Failed to load top 5 items', err);
      setMessages([]);
    }
  };

  /**
  * @description Send user message to AI and append AI response to chat
  * Handles loading state, error fallback, and message formatting
  * @author Ashish Sharma
  */
  const handleSend = async () => {
    const currentCache = autoSendJsonData ?? getScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA) ?? {};
    const trimmed = inputValue.trim();
    if (!trimmed || !hasProviders || !selectedModel || isLoading) return;

    const userMessage = {
      id: generateAiChatBotMsgId('user'),
      text: trimmed,
      sender: 'user',
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const question = autoSendJsonData
        ? `${trimmed}\n\nRespond as a friendly cloud advisor. Do not use greetings or re-introduce the instance. Continue the conversation directly. Do not mention JSON or field names.`
        : trimmed;

      const response = await AIChatbotApiService.askAiChatbot({
        accessKey: providerKeys[selectedModel],
        provider: selectedModel,
        question,
        jsonData: currentCache,
        sourceFrom: currentPath,
      });

      const aiText = formatAIResponse(response);

      setMessages(prev => [
        ...prev,
        { id: generateAiChatBotMsgId('ai'), text: aiText, sender: 'ai' },
      ]);
    } catch (err) {
      DebugService.error('Chat API failed', err);
      setMessages(prev => [
        ...prev,
        {
          id: generateAiChatBotMsgId('ai'),
          text: 'Something went wrong. Please try again.',
          sender: 'ai',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
  * @description Handle Enter key press to send chat message
  * Prevents newline on Enter without Shift
  * @author Ashish Sharma
  */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
  * @description Handle click on a suggested question - show question as user + pre-fetched answer as AI (no API call)
  */
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.clicked) return;

    const userMessage = {
      id: generateAiChatBotMsgId('user'),
      text: suggestion.text,
      sender: 'user',
    };

    const aiMessage = {
      id: generateAiChatBotMsgId('ai'),
      text: suggestion.answer || 'No answer available for this question.',
      sender: 'ai',
    };

    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.id === suggestion.id
          ? { ...msg, clicked: true }
          : msg
      );
      return [...updatedMessages, userMessage, aiMessage];
    });
  };

  /**
  * @description Fetch and display full chat history for the current page
  * Formats stored user and AI messages in chronological order
  * @author Ashish Sharma
  */
  const handleViewFullHistory = async () => {
    setOptionsAnchorEl(null);

    try {
      const history = await AIChatbotApiService.getAiChatbotHistory(currentPath);

      if (history?.length) {
        const formatted = [];

        [...history].reverse().forEach(item => {
          formatted.push(
            { id: `user-full-${item.id}`, text: item.question, sender: 'user' },
            { id: `ai-full-${item.id}`, text: formatAIResponse(item.response), sender: 'ai' }
          );
        });

        setMessages(formatted);
      }
    } catch (err) {
      DebugService.error('Failed to fetch full chat history', err);
    }
  };

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch AI ChatBot providers when drawer opens
  useEffect(() => {
    if (!open) return;
    autoSendFiredRef.current = false;
    fetchProviders();
  }, [open]);

  // Auto-send prompt once providers are ready (used by Details button flow)
  useEffect(() => {
    if (
      !autoSendPrompt ||
      !hasProviders ||
      !selectedModel ||
      !providerKeys[selectedModel] ||
      autoSendFiredRef.current
    ) return;

    autoSendFiredRef.current = true;

    setMessages([]);
    setIsLoading(true);

    AIChatbotApiService.askAiChatbot({
      accessKey: providerKeys[selectedModel],
      provider: selectedModel,
      question: autoSendPrompt,
      jsonData: autoSendJsonData ?? {},
      sourceFrom: currentPath,
    })
      .then((response) => {
        const aiText = formatAIResponse(response);
        setMessages((prev) => [
          ...prev,
          { id: generateAiChatBotMsgId('ai'), text: aiText, sender: 'ai' },
        ]);
      })
      .catch(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: generateAiChatBotMsgId('ai'),
            text: 'Something went wrong. Please try again.',
            sender: 'ai',
          },
        ]);
      })
      .finally(() => setIsLoading(false));
  }, [hasProviders, selectedModel, providerKeys, autoSendPrompt]);

  // Fetch quations first time when AiChatbot Loads
  useEffect(() => {
  // Skip entirely when auto-send is active — it handles its own message flow
  if (autoSendPrompt) return;

  setCurrentPath(location.pathname);
  const basePath = '/' + location.pathname.split('/')[1];

  const currentCache = getScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA) ?? {};

  // Skip if conditions not met
  if (
    !currentCache ||
    messages.length > 0 ||
    currentPath === CONST_ROUTE_PATH.ADD_ACCOUNT ||
    !currentPath ||
    !providerKeys[selectedModel]
  ) return;

  if (AI_CHATBOT_DISABLED_ROUTES.includes(basePath)) return;

  const delay = isTopLevelEmpty(currentCache) ? 10000 : 3000;

  setIsLoadingSuggestions(true);   // ← show skeletons

  const timer = setTimeout(() => {
    fetchTopQuestions(getScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA))
      .finally(() => {
        setIsLoadingSuggestions(false);   // ← hide skeletons when done
      });
  }, delay);

  return () => clearTimeout(timer);
}, [open, location.pathname, providerKeys, currentPath, selectedModel]);

  // Clears stored AI chatbot request data when navigating to routes where AI chat is disabled.
  useEffect(() => {
    const basePath = '/' + location.pathname.split('/')[1];

    setMessages([]);
    if (AI_CHATBOT_DISABLED_ROUTES.includes(basePath)) {
      setScopedItem(
        STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA,{
          message: "You are in general assistance mode. Provide the most helpful way you can assist with right now."
        }
      );
    }else{
      setScopedItem(
        STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA,null
      );
    }
    
  }, [location.pathname]);

  if (!open) return null;

  const chatbotDisableContent =  (
    <Box className="ai-chatbot-container ai-chatbot-centered-disabled">
    <SmartToyIcon 
      sx={{ fontSize: 64, color: 'action.disabled', mb: 3 }} 
    />
    
    <Typography variant="h5" gutterBottom fontWeight={600}>
        AI Assistant not available here
    </Typography>
    
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 360 }}>
        We're working hard to bring AI assistance to this section soon.
    </Typography>
    
    <Typography variant="body2" color="text.secondary">
        Coming soon ✦
    </Typography>
    <Button
        variant="outlined"
        size="medium"
        sx={{ mt: 4 }}
        onClick={onClose}
      >
        Close
    </Button>
    </Box>
  );

  const chatbotContent = (
    <Box className={`ai-chatbot-container${modalMode ? ' ai-chatbot-modal' : ''}`}>
        {/* Header */}
        <Box className="ai-chatbot-header">
          <Box className="ai-chatbot-header-title">
            <ChatIcon className="ai-chatbot-header-icon" />
            <Typography variant="h6" fontWeight={600}>
              AI Assistant
            </Typography>
          </Box>
          <Box className="ai-chatbot-header-actions">
            <IconButton size="small" onClick={e => setOptionsAnchorEl(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={optionsAnchorEl}
              open={Boolean(optionsAnchorEl)}
              onClose={() => setOptionsAnchorEl(null)}
            >
              <MenuItem onClick={handleViewFullHistory}>View Full History</MenuItem>
            </Menu>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Messages */}
        <Box className="ai-chatbot-messages">
          {isLoadingSuggestions ? (

            <TopQuestionsLoader count={5} />

          ) : autoSendPrompt && isLoading && messages.length === 0 ? (

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
              <CircularProgress size={36} />
              <Typography variant="body2" color="text.secondary">Analysing instance data...</Typography>
            </Box>

          ) : messages.length === 0 ? (
          
            hasProviders ? (
              <Typography variant="body1" className="ai-chatbot-empty-message">
                Start a conversation with AI
              </Typography>
            ) : (
              <Box className="ai-chatbot-no-provider">
                <SmartToyIcon className="ai-chatbot-no-provider-icon" />
                <Typography variant="h6" gutterBottom color="text.secondary">
                  No AI Provider Configured
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  To use the AI Assistant, please add an API key for at least one provider.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setScopedItem(STORAGE_KEYS.CURRENT_ROUTE, CONST_ROUTE_PATH.MY_PROFILE);
                    navigate(CONST_ROUTE_PATH.MY_PROFILE);
                  }}
                >
                  Go to Settings
                </Button>
              </Box>
            )
          
          ) : (
          
            <Box className="ai-chatbot-messages-list">
              {messages.map(msg => (
                <Box
                  key={msg.id}
                  className={msg.sender === 'user' ? 'message-user-container' : 'message-ai-container'}
                  onClick={msg.isSuggestion && !msg.clicked ? () => handleSuggestionClick(msg) : undefined}
                  sx={msg.isSuggestion && !msg.clicked ? { cursor: 'pointer' } : {}}
                >
                  <Paper
                    elevation={1}
                    className={msg.sender === 'user' ? 'message-user-bubble' : 'message-ai-bubble'}
                  >
                    {msg.sender === 'ai' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {msg.text}
                      </ReactMarkdown>
                    ) : (
                      <Typography variant="body2">
                        {msg.text}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Box>

          )}
        </Box>

        {/* Input Area */}
        <Box className="ai-chatbot-input-section">
          {/* <Box className="ai-chatbot-provider-selector">
            <Button
              size="small"
              onClick={e => setAnchorEl(e.currentTarget)}
              endIcon={<KeyboardArrowDownIcon />}
              disabled={!hasProviders}
            >
              {hasProviders
                ? models.find(m => m.value === selectedModel)?.label || 'Select Provider'
                : 'No Providers Available'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              {models.map(model => (
                <MenuItem
                  key={model.value}
                  selected={model.value === selectedModel}
                  onClick={() => {
                    setSelectedModel(model.value);
                    setAnchorEl(null);
                  }}
                >
                  {model.label}
                </MenuItem>
              ))}
            </Menu>
          </Box> */}

          <Paper
            className={`ai-chatbot-input-paper ${!hasProviders || isLoading ? 'ai-chatbot-input-disabled' : ''}`}
          >
            <TextField
              fullWidth
              multiline
              maxRows={5}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isLoadingSuggestions
                  ? "Loading suggested questions..."
                  : isLoading
                    ? "AI is thinking..."
                    : hasProviders
                      ? "Message AI..."
                      : "Add a provider key to start"
              }
              variant="standard"
              InputProps={{ disableUnderline: true }}
              disabled={!hasProviders || isLoading || isLoadingSuggestions}
              sx={{
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '14px',
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!inputValue.trim() || !hasProviders || isLoading || isLoadingSuggestions}
              color="primary"
              sx={{
                p: 0,
                m: 'auto',
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Paper>

          {!hasProviders && (
            <Typography variant="caption" color="text.secondary" className="ai-chatbot-input-hint">
              Add an API key in Settings → AI Providers
            </Typography>
          )}
        </Box>
      </Box>
  );

  const basePath = '/' + location.pathname.split('/')[1];
  const isChatbotDisabled = AI_CHATBOT_DISABLED_ROUTES.includes(basePath);
  const contentToRender = isChatbotDisabled ? chatbotDisableContent : chatbotContent;

  return isDesktop ? (
    contentToRender
  ) : (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
    >
      {contentToRender}
    </Drawer>
  );
};

export default AIChatbot;